import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, query, state } from 'lit/decorators.js';
import componentStyles from './tufts-alpha-listing.css.lit';
import { ResizeController } from '../../controllers/resize-controller';

export type TuftsAlphaListingLink = { text: string; url: string };

export type TuftsAlphaListingItem = {
  letter: string;
  url?: string;
  active: boolean;
  links: TuftsAlphaListingLink[];
};

/**
 * The TuftsAlphaListing component
 * @element tufts-alpha-listing the a-z listing component
 */
@customElement('tufts-alpha-listing')
export class TuftsAlphaListing extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  resizeController: ResizeController = new ResizeController(this);

  @query('#alpha-listing-root')
  root: HTMLElement;

  @state() nodesWithletterList: HTMLElement[] = [];

  firstUpdated(): void {
    this.checkIfWeHaveElements();
  }

  // Checks if we have elements and creates a list of alphabetically ordered elements.
  checkIfWeHaveElements(): void {
    this.nodesWithletterList = [];
    // Find all services-listing-header id elements
    const tuftsAlphaListing: NodeListOf<Element> = this.querySelectorAll(
      'div[id^="services-listing-header"]'
    );

    if (tuftsAlphaListing.length > 1) {
      const htmlTag: HTMLHtmlElement | null = document.querySelector('html');
      htmlTag?.setAttribute('style', 'scroll-behavior: smooth;');
    }

    const alphabet = [
      'a',
      'b',
      'c',
      'd',
      'e',
      'f',
      'g',
      'h',
      'i',
      'j',
      'k',
      'l',
      'm',
      'n',
      'o',
      'p',
      'q',
      'r',
      's',
      't',
      'u',
      'v',
      'w',
      'x',
      'y',
      'z',
    ];

    // Iterate over each letter in the alphabet
    alphabet.forEach(letter => {
      // Find the first element in tuftsAlphaListing whose title starts with the current letter
      const container = Array.from(tuftsAlphaListing).find(element => {
        const firstLetter = element.textContent?.trim().charAt(0).toLowerCase();
        return firstLetter === letter;
      });

      // Create a container for the list item
      const liContainer = document.createElement('li');
      liContainer.className = 'tufts-alpha-listing--mini-item';

      if (container) {
        // If a matching element is found, create an anchor element
        const idElement = container.getAttribute('id');
        const titleElement = container.textContent?.trim();

        if (idElement && titleElement) {
          const anchor = document.createElement('a');
          anchor.innerText = titleElement;
          anchor.setAttribute('href', `#${idElement}`);
          anchor.className = 'tufts-alpha-listing--mini-link';

          // Append the anchor to the list item container
          liContainer.appendChild(anchor);
        }
      } else {
        // If no matching element is found, create a span element with the missing letter
        const span = document.createElement('span');
        span.innerText = letter.toLocaleUpperCase();
        span.className = 'tufts-alpha-listing--missing-letter';

        // Append the span to the list item container
        liContainer.appendChild(span);
      }

      // Add the list item container to the array
      this.nodesWithletterList.push(liContainer);
    });

    this.templateDesktop();
  }

  templateDesktop() {
    // Check if there are multiple elements in the slot
    if (this.nodesWithletterList.length > 1) {
      // Create the necessary containers and elements
      const ulContainer = document.createElement('ul');
      ulContainer.className = 'tufts-alpha-listing--list-mini ';
      // Append the li containers to the ul container
      ulContainer.append(...this.nodesWithletterList);
      // InserBefore element and form the details container to the root element
      const element = this.querySelector(
        '#edit-field-servline-is-pediatrics-value--wrapper'
      );
      // "this" refers to the current instance of the component's class,
      // so replacing document with "this" will only search within the component and not the entire site

      const form = this.querySelector('form.views-exposed-form');

      if (form && element) {
        form.insertBefore(ulContainer, element);
      }
    }
  }

  checkMobileLayout() {
    const isMobileLayout = this.resizeController.currentBreakpointRange === 0;

    const mainContainer = this.querySelector('.views-element-container');

    if (mainContainer) {
      if (isMobileLayout) {
        // Add the 'isMobile' class to mainContainer if isMobileLayout is true
        mainContainer.classList.add('isMobile');
      } else {
        // Remove the 'isMobile' class from mainContainer if isMobileLayout is false
        mainContainer.classList.remove('isMobile');
      }
    }
  }

  render(): TemplateResult {
    this.checkMobileLayout();
    return html`
      <section id="alpha-listing-root">
        <slot></slot>
      </section>
    `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'tufts-alpha-listing': TuftsAlphaListing;
  }
}
